import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'Is any 9%+ yield safe?',
    a: 'Some are, some aren\'t — and the math is structural, not magical. BDCs like ARCC distribute regulated lending income. Covered-call ETFs like JEPI write options premium on a stable underlying. Mortgage REITs like AGNC earn the spread between borrowing short and lending long. Each has a defensible mechanism. The dividend-trap names also yield 9%+ but get there by paying out more than they earn — they\'re the ones that cut. The structural test separates them.',
  },
  {
    q: 'What\'s the difference between BDC NII coverage and a regular payout ratio?',
    a: 'BDCs (Business Development Companies) must by law distribute at least 90% of their taxable income. So a "payout ratio" near 100% is a STRUCTURAL requirement, not a warning. The real metric is Net Investment Income (NII) coverage — does the BDC earn enough NII to cover the distribution? ARCC has covered its dividend with NII for 50+ consecutive quarters, which is why a 10%+ yield isn\'t a trap.',
  },
  {
    q: 'What about covered-call ETFs like JEPI?',
    a: 'JEPI / JEPQ / QYLD / SPYI all generate income by selling call options on stocks they own. The premium becomes the dividend. The structural trade-off: in flat or down markets, the premium income is great. In strongly UP markets, the calls cap the fund\'s upside, so total return lags the index. Their high yields are absolutely real and sustainable — but you give up some appreciation to get them.',
  },
  {
    q: 'How does DiviDrip help me check if a high yield is safe?',
    a: 'The Triangle Score is the single best filter — it bundles payout ratio (or NII coverage for BDCs), FCF coverage, debt trends, and streak quality. A 12% yield with a green Triangle Score and stable historical metrics is structurally different from a 12% yield with a red Triangle and falling cash flow. The Dividend Traps guide also walks through the red-flag pattern in detail.',
  },
];

export default function Learn9PercentYieldSafe() {
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } })),
  };
  return (
    <LearnPage
      title="Is a 9%+ Yield Safe? Honest Examples of Sustainable High Yield | DiviDrip"
      description="Not every double-digit yield is a trap. ARCC, AGNC, JEPI, JEPQ, QYLD, SPYI — the structural reasons their high payouts ARE sustainable, the four things to check before adding any 9%+ name to your portfolio."
      canonical="https://twylightcrow.com/learn/is-9-percent-yield-safe"
      guidePath="/learn/is-9-percent-yield-safe"
      sectionLabel="Metrics & Evaluation"
      sectionAnchor="metrics"
      kicker="Learn · High-yield reality check"
      h1="Is a 9%+ Yield Safe? Honest Examples of Sustainable High Yield"
      datePublished="2026-05-24"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/dividend-traps', label: 'Dividend trap stocks — spotting yields that lie' },
        { to: '/learn/payout-ratio', label: 'The payout ratio — your first safety check' },
        { to: '/learn/dividend-triangle', label: 'Reading the Dividend Triangle score' },
        { to: '/learn/reits-vs-bdcs-vs-cefs', label: 'REITs vs BDCs vs CEFs' },
        { to: '/learn/covered-call-etfs', label: 'Covered-call ETFs explained' },
      ]}
    >
      <p>
        Conventional dividend wisdom says &quot;anything over 6% is suspicious;
        anything over 9% is a trap.&quot; That&apos;s a useful heuristic for
        regular C-corp blue chips. It&apos;s also <strong>wrong for entire
        categories of legitimately high-yielding instruments</strong>. BDCs,
        mortgage REITs, covered-call ETFs, and certain CEFs are STRUCTURED to
        pay 8-14% and have done so reliably for years.
      </p>
      <p>
        Let&apos;s walk through real examples — yields verified live from the
        DiviDrip database — and the structural reasons each one is sustainable.
      </p>

      <aside className="not-prose my-6 rounded-lg border border-sky-300/40 dark:border-sky-700/40 bg-sky-50/60 dark:bg-sky-950/30 p-4">
        <p className="text-sm font-semibold text-sky-900 dark:text-sky-200 mb-1">
          ETF readers — read this first
        </p>
        <p className="text-sm text-sky-900/85 dark:text-sky-200/85">
          Most of the high-yielders below (JEPI, JEPQ, QYLD, XYLD, SPYI) are
          ETFs. The Triangle Score won&apos;t help you judge them — ETFs have
          no underlying revenue or EPS so the math is single-leg only.
          For ETF judgement use expense ratio + AUM + the Div Growth tab.
          See{' '}
          <Link to="/learn/evaluating-dividend-etfs" className="underline">
            How to Evaluate Dividend ETFs
          </Link>
          {' '}for the full framework.
        </p>
      </aside>

      <h2>Six 9%+ yielders that are NOT traps</h2>
      <table className="learn-table">
        <thead>
          <tr><th>Ticker</th><th>Yield</th><th>Type</th><th>Why it works</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>AGNC</strong></td>
            <td>~13.9%</td>
            <td>Mortgage REIT</td>
            <td>Earns the spread between borrowing short and lending long on agency MBS. As long as the yield curve isn&apos;t deeply inverted, the spread funds the dividend.</td>
          </tr>
          <tr>
            <td><strong>SPYI</strong></td>
            <td>~12.5%</td>
            <td>Covered-call ETF (S&amp;P 500)</td>
            <td>NEOS sells call options on the S&amp;P; premium becomes the monthly dividend. Tax-efficient structure using SPX index options.</td>
          </tr>
          <tr>
            <td><strong>QYLD</strong></td>
            <td>~11.9%</td>
            <td>Covered-call ETF (NASDAQ-100)</td>
            <td>Global X writes at-the-money calls every month on QQQ. Caps upside but generates predictable premium income.</td>
          </tr>
          <tr>
            <td><strong>XYLD</strong></td>
            <td>~11.0%</td>
            <td>Covered-call ETF (S&amp;P 500)</td>
            <td>Same structure as QYLD but on the S&amp;P 500. Premium income via systematic call writing.</td>
          </tr>
          <tr>
            <td><strong>JEPQ</strong></td>
            <td>~10.4%</td>
            <td>Equity-linked notes (NASDAQ)</td>
            <td>JPMorgan&apos;s actively-managed Nasdaq cousin to JEPI. Uses ELNs instead of selling calls directly. Lower index correlation, similar payout mechanism.</td>
          </tr>
          <tr>
            <td><strong>ARCC</strong></td>
            <td>~10.3%</td>
            <td>BDC</td>
            <td>Lends to private middle-market companies. Required to distribute 90%+ of NII. 50+ consecutive quarters of NII covering the distribution.</td>
          </tr>
          <tr>
            <td><strong>JEPI</strong></td>
            <td>~9.5%</td>
            <td>Equity-linked notes (S&amp;P)</td>
            <td>JPMorgan&apos;s flagship — equity-linked notes generate option premium. AUM has grown to $30B+ since 2020 launch.</td>
          </tr>
        </tbody>
      </table>

      <h2>The four questions that separate sustainable from trap</h2>
      <ol>
        <li>
          <strong>What&apos;s the structural source of the yield?</strong> BDCs:
          lending NII. mREITs: yield-curve spread. Covered-call ETFs: option
          premium. Energy MLPs: pipeline tolls. If you can&apos;t name the
          structural source, you&apos;re looking at a trap. If you can, you&apos;re
          looking at a specialised vehicle.
        </li>
        <li>
          <strong>Is the metric appropriate for the structure?</strong> Regular
          payout ratio for a BDC is meaningless (they MUST pay 90%+). Use NII
          coverage instead. Same for REITs (use FFO / AFFO). MLPs use DCF
          coverage. Apply the right yardstick. See{' '}
          <Link to="/learn/payout-ratio">the payout ratio guide</Link> for the
          sector-by-sector versions.
        </li>
        <li>
          <strong>How has the dividend behaved historically?</strong> Has it
          been stable for 5+ years? Has it grown? Or has it been cut multiple
          times? AGNC has cut. ARCC has not. SPYI is too new to fully judge
          (launched 2022) but coverage looks solid. History matters.
        </li>
        <li>
          <strong>Does the Triangle Score back it up?</strong> Open the ticker
          on DiviDrip. The Triangle bundles payout coverage, FCF coverage, debt
          trends, and streak. A green Triangle on a 12% yielder is a real signal
          — the structural math works. A red Triangle on a 12% yielder is
          screaming &quot;trap&quot;.
        </li>
      </ol>

      <h2>The trap pattern — for contrast</h2>
      <p>
        Sustainable high yields share a pattern. Dividend traps share a different
        one:
      </p>
      <ul>
        <li>Headline yield is HIGH because the STOCK has fallen, not because the dividend has grown.</li>
        <li>The traditional payout ratio is over 100% (and there&apos;s no structural reason like REIT/BDC status to explain it).</li>
        <li>Free cash flow is declining year over year.</li>
        <li>Debt is rising faster than earnings.</li>
        <li>The Triangle Score is red and falling.</li>
      </ul>
      <p>
        This is what GE looked like in 2017. Walgreens in 2024. Lumen Technologies
        in 2022. The yields were 10-15%; the structures were unsustainable.
        Every name on this list cut their dividend within 12 months. See{' '}
        <Link to="/learn/dividend-cuts-of-the-decade">Dividend Cuts of the
        Decade</Link> for the case studies.
      </p>

      <h2>The portfolio integration</h2>
      <p>
        Even after vetting, high-yielders carry concentration risk. Most income
        advisors recommend limiting structured high-yielders to <strong>15-25%
        of an income portfolio</strong>, with the rest in quality{' '}
        <Link to="/learn/dividend-kings-vs-aristocrats-vs-achievers">Aristocrats
        and Kings</Link>. The high-yielders boost current cash flow; the
        compounders provide growth + safety. Together they balance.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Bottom line</h2>
      <p>
        Not all 9%+ yields are traps. The ones with a STRUCTURAL source (BDC
        NII, mREIT spread, option premium) and a clean coverage record are
        legitimate income tools — different in character from your KO and JNJ,
        but real. The ones WITHOUT structural support are exactly the traps the
        market keeps repeating. The Triangle Score on DiviDrip is your single
        best filter for telling them apart.
      </p>
    </LearnPage>
  );
}
